import React from 'react'
import { makeStyles } from 'tss-react/mui'
import {
  HeroTextSection as HeroTextSectionType,
  HTSVariants,
} from '../../types/heroTextSection'
import HeroTextSectionCentered from './HeroTextSectionCentered'
import getColor, { getTextColor } from '../../utils/colors'
import useBrand from '../../hooks/useBrand'
import formatContentfulClasses from '../../utils/customClasses'
import { withHideOn } from '../../hocs/withHideOn'

export interface HeroTextSectionProps {
  row: HeroTextSectionType
  sectionClass?: string
}

const HeroTextSection: React.FC<HeroTextSectionProps> = ({
  row,
  sectionClass,
}) => {
  const { classes, cx } = useStyles(row)
  const brand = useBrand()

  switch (row?.variant) {
    case HTSVariants.Centered:
      return (
        <HeroTextSectionCentered
          displayClass={cx({
            [classes.single]: true,
            [classes.centered]: true,
            [classes.background]: true,
            [classes.paddingTopZero]: row.paddingUpdate === 'ZeroTopPadding',
            [classes.paddingBottomZero]:
              row.paddingUpdate === 'ZeroBottomPadding',
            [classes.paddingZero]: row.paddingUpdate === 'ZeroTopBottomPadding',
          })}
          sectionClass={cx(
            sectionClass,
            ...formatContentfulClasses(row.codeClasses)
          )}
          titleClass={cx({
            [classes.subtitle]: true,
            [classes.subtitleCurrentColor]: brand.name !== 'ZipJob',
            [classes.subtitleTR]: brand.name !== 'ZipJob',
          })}
          row={row}
        />
      )
    case HTSVariants.CenteredVideo:
      return (
        <HeroTextSectionCentered
          displayClass={cx({
            [classes.videoCentered]: true,
            [classes.centered]: true,
            [classes.background]: true,
            [classes.paddingTopZero]: row.paddingUpdate === 'ZeroTopPadding',
            [classes.paddingBottomZero]:
              row.paddingUpdate === 'ZeroBottomPadding',
            [classes.paddingZero]: row.paddingUpdate === 'ZeroTopBottomPadding',
          })}
          sectionClass={cx(
            sectionClass,
            ...formatContentfulClasses(row.codeClasses)
          )}
          titleClass={cx({
            [classes.subtitle]: true,
            [classes.subtitleCurrentColor]: brand.name !== 'ZipJob',
            [classes.subtitleTR]: brand.name !== 'ZipJob',
          })}
          row={row}
        />
      )
    case HTSVariants.CenteredSkinny:
      return (
        <HeroTextSectionCentered
          displayClass={cx({
            [classes.single]: true,
            [classes.skinnyCentered]: true,
            [classes.background]: true,
          })}
          sectionClass={cx(
            sectionClass,
            ...formatContentfulClasses(row.codeClasses)
          )}
          titleClass={cx({
            [classes.subtitle]: true,
            [classes.subtitleCurrentColor]: brand.name !== 'ZipJob',
            [classes.subtitleTR]: brand.name !== 'ZipJob',
          })}
          row={row}
        />
      )
    case HTSVariants.Left:
      return (
        <HeroTextSectionCentered
          displayClass={cx({
            [classes.single]: true,
            [classes.left]: true,
            [classes.background]: true,
            [classes.paddingTopZero]: row.paddingUpdate === 'ZeroTopPadding',
          })}
          sectionClass={cx(
            sectionClass,
            ...formatContentfulClasses(row.codeClasses)
          )}
          titleClass={cx({
            [classes.subtitle]: true,
            [classes.subtitleCurrentColor]: brand.name !== 'ZipJob',
            [classes.subtitleTR]: brand.name !== 'ZipJob',
          })}
          row={row}
        />
      )
    default:
      return null
  }
}

const useStyles = makeStyles<{ backgroundColor: string }>()(
  (theme, { backgroundColor }) => {
    const bgColor = getColor(backgroundColor, theme)
    const textColor = getTextColor(backgroundColor, theme)

    return {
      single: {
        maxWidth: '100%',
        [theme.breakpoints.down('md')]: {
          width: '100%',
        },
      },

      background: {
        backgroundColor: bgColor,
        color: textColor,
      },
      centered: {
        textAlign: 'center',
      },
      left: {
        textAlign: 'left',
      },
      centeredDark: {
        textAlign: 'center',
        maxWidth: '100%',
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.background.default,
      },
      videoCentered: {
        background: 'linear-gradient(to bottom, #fff 85%, #fafafc 15%)',
        textAlign: 'center',
        maxWidth: '100%',
        [theme.breakpoints.down('md')]: {
          width: '100%',
        },
      },
      skinnyCentered: {
        textAlign: 'center',
        paddingLeft: 'calc((100% - 60rem)/2 + 5rem) !important',
        paddingRight: 'calc((100% - 60rem)/2 + 5rem) !important',
        [theme.breakpoints.down('md')]: {
          width: '100%',
          paddingLeft: '6% !important',
          paddingRight: '6% !important',
        },
      },
      columns: {
        flexDirection: 'row',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
          justifyContent: 'center',
        },
      },
      columnsReverse: {
        flexDirection: 'row-reverse',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
          justifyContent: 'center',
        },
      },
      subtitle: {
        color: theme.palette.secondary.dark,
        textTransform: 'uppercase',
        fontSize: '.8rem',
        letterSpacing: '.9px',
        fontWeight: 'bold',
        marginBottom: '.7rem',
        [theme.breakpoints.down('md')]: {
          marginBottom: '.7rem',
        },
      },
      subtitleCurrentColor: {
        color: 'currentColor',
      },
      subtitleTR: {
        fontSize: '.88rem',
        letterSpacing: '.09rem',
        margin: '0',
      },
      paddingTopZero: {
        paddingTop: '0 !important',
      },
      paddingBottomZero: {
        paddingBottom: '0',
      },
      paddingZero: {
        paddingTop: '0',
        paddingBottom: '0',
      },
    }
  }
)

export default withHideOn(HeroTextSection)
